import {
  Box,
  CardsList,
  Item,
  Table,
  useIsMobile,
} from '@brunommsilva/react-foundations';
import { HelpOutlined } from '@mui/icons-material';
import { Box as MuiBox, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_ICON_MAP } from '../../config/category-icons';
import { TEST_IDS } from '../../config/test-ids';
import { Category } from '../../models/categories';
import { Delete, Edit } from '../icons';
import DeleteCategoryModal from './delete-category-modal';
import EditCategoryModal from './edit-category-modal';
interface CategoriesListProps {
  categories: Category[];
  isLoading: boolean;
  isError?: boolean;
}

const CategoriesList = ({
  categories,
  isLoading,
  isError,
}: CategoriesListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [currentCategory, setCurrentCategory] = useState<Category>();
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

  const onEditCategory = (category: Category) => {
    setCurrentCategory(category);
    setShowEditCategoryModal(true);
  };

  const closeEditCategoryModal = () => {
    setShowEditCategoryModal(false);
  };

  const onDeleteCategory = (category: Category) => {
    setCurrentCategory(category);
    setShowDeleteCategoryModal(true);
  };

  const closeDeleteCategoryModal = () => {
    setShowDeleteCategoryModal(false);
  };

  const buildColumns = () => {
    return [
      {
        id: 'name',
        label: t('CATEGORY_NAME'),
        format: (category: Category) => {
          const IconComp =
            CATEGORY_ICON_MAP[category.icon || 'other'] || HelpOutlined;
          return (
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconComp sx={{ color: 'text.secondary', fontSize: 20 }} />
              <span data-testid={TEST_IDS.categories.name}>
                {category.name}
              </span>
            </MuiBox>
          );
        },
      },
      {
        id: 'description',
        label: t('CATEGORY_DESCRIPTION'),
        format: (category: Category) => category.description || '-',
      },
      {
        id: 'ACTIONS',
        align: 'right',
        format: (category: Category) => (
          <>
            <IconButton
              color="primary"
              onClick={() => onEditCategory(category)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              data-testid={TEST_IDS.categories.deleteButton}
              onClick={() => onDeleteCategory(category)}
            >
              <Delete />
            </IconButton>
          </>
        ),
      },
    ];
  };

  const renderDeleteCategoryModal = () => {
    return (
      <DeleteCategoryModal
        show={showDeleteCategoryModal}
        category={currentCategory}
        onDeleted={closeDeleteCategoryModal}
        onClose={closeDeleteCategoryModal}
      />
    );
  };

  const renderEditCategoryModal = () => {
    return (
      <EditCategoryModal
        show={showEditCategoryModal}
        category={currentCategory}
        onEdited={closeEditCategoryModal}
        onClose={closeEditCategoryModal}
      />
    );
  };

  const renderEmpty = () => (
    <Box direction="column" alignItems="center">
      <Item>{t('NO_CATEGORIES_FOUND_MESSAGE')}</Item>
    </Box>
  );

  const renderCards = () => (
    <CardsList
      isLoading={isLoading}
      isError={isError}
      items={categories}
      renderItem={(category) => {
        const IconComp =
          CATEGORY_ICON_MAP[category.icon || 'other'] || HelpOutlined;

        return (
          <MuiBox
            key={category.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'background.paper',
            }}
          >
            <MuiBox sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <IconComp sx={{ color: 'text.secondary', fontSize: 24 }} />
              <MuiBox sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {category.name}
                </Typography>
                {category.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {category.description}
                  </Typography>
                )}
              </MuiBox>
            </MuiBox>

            <MuiBox sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEditCategory(category)}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteCategory(category)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </MuiBox>
          </MuiBox>
        );
      }}
    />
  );

  const renderTable = () => (
    <Table
      isLoading={isLoading}
      isError={isError}
      columns={buildColumns()}
      rows={categories}
    />
  );

  const renderList = () => {
    if (!isLoading && !isError && !categories?.length) {
      return renderEmpty();
    }
    if (isMobile) {
      return renderCards();
    }
    return renderTable();
  };

  return (
    <>
      {renderList()}
      {renderDeleteCategoryModal()}
      {renderEditCategoryModal()}
    </>
  );
};

export default CategoriesList;
